import { FC, useEffect, useState } from 'react';
import { TextInput } from '@carbon/react';
import { SidePanel } from '@carbon/ibm-products';
import { useExclusiveDrawer } from './exclusive-drawer-hooks';
import style from './panel-content.module.scss';

interface Attrs {
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
}

interface Props {
  open: boolean;
  mode?: 'add' | 'edit';
  initial?: Partial<Attrs>;
  onClose: () => void;
  onSubmit: (attrs: Attrs) => Promise<void>;
}

const EMPTY: Attrs = {
  street_address: '',
  city: '',
  state: '',
  zip_code: '',
};

export const AddDocumentAddressPanel: FC<Props> = ({
  open,
  mode = 'add',
  initial,
  onClose,
  onSubmit,
}) => {
  const [values, setValues] = useState<Attrs>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const pageContentSelector = useExclusiveDrawer(
    mode === 'edit' ? 'document-address-edit' : 'document-address-add',
    open,
    onClose
  );

  useEffect(() => {
    if (!open) {
      setValues(EMPTY);
    } else if (initial) {
      setValues({
        street_address: initial.street_address ?? '',
        city: initial.city ?? '',
        state: initial.state ?? '',
        zip_code: initial.zip_code ?? '',
      });
    } else {
      setValues(EMPTY);
    }
  }, [open, initial]);

  const valid = Object.values(values).some((v) => v.trim().length > 0);

  const handleSave = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  const isEdit = mode === 'edit';
  const title = isEdit ? 'Edit Document Address' : 'Add Document Address';
  const subtitle = isEdit
    ? 'Update the parsed components for this non-party address.'
    : 'Capture an address found on the document but not attributed to any party.';
  const submitLabel = isEdit
    ? submitting
      ? 'Saving…'
      : 'Save Changes'
    : submitting
      ? 'Saving…'
      : 'Add Address';

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title={title}
      subtitle={subtitle}
      placement="right"
      size="md"
      slideIn
      selectorPageContent={pageContentSelector}
      actions={[
        { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
        {
          kind: 'primary' as const,
          label: submitLabel,
          disabled: !valid || submitting,
          onClick: handleSave,
        },
      ]}
    >
      <div className={style.panelForm}>
        <TextInput
          id="add-doc-addr-street"
          labelText="Street Address"
          value={values.street_address}
          onChange={(e) =>
            setValues({ ...values, street_address: e.target.value })
          }
        />
        <TextInput
          id="add-doc-addr-city"
          labelText="City"
          value={values.city}
          onChange={(e) => setValues({ ...values, city: e.target.value })}
        />
        <TextInput
          id="add-doc-addr-state"
          labelText="State"
          value={values.state}
          onChange={(e) => setValues({ ...values, state: e.target.value })}
        />
        <TextInput
          id="add-doc-addr-zip"
          labelText="ZIP Code"
          value={values.zip_code}
          onChange={(e) => setValues({ ...values, zip_code: e.target.value })}
        />
      </div>
    </SidePanel>
  );
};
